import React from 'react';
import { Box, Chip as MuiChip, ChipProps as MuiChipProps } from '@mui/material';
import { alpha, styled, SxProps, Theme } from '@mui/material/styles';
import { chipClasses } from '@mui/material/Chip';
import { VideoTileLayoutMode } from 'features/layout/types';

export const Chip = styled(MuiChip, {
  shouldForwardProp: (prop) => prop !== 'handRaised',
})(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: alpha(theme.palette.common.black, 0.7),
  fontSize: theme.typography.pxToRem(14),
  height: 26,
  textTransform: 'capitalize',
  transition: 'none',
  userSelect: 'none',
  [`&.${chipClasses.sizeMedium} > .${chipClasses.label}`]: {
    padding: theme.spacing(0, 4),
  },
}));

type StreamBadgeProps = {
  label?: string | null;
  badgeCount?: number;
  layout?: VideoTileLayoutMode;
  sx?: SxProps<Theme>;
  ChipProps?: MuiChipProps;
  className?: string;
};

const BADGE_ITEM_WIDTH = 32;

const topPositionSx = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'center',
  position: 'absolute',
  left: 0,
  width: '100%',
  top: theme.spacing(2),
  textAlign: 'center',
  padding: theme.spacing(0, 2),
  zIndex: 3,
  '&[data-badge-count="1"], &[data-badge-count="2"]': {
    [`& > .${chipClasses.root}`]: {
      maxWidth: '100%',
      minWidth: '40px',
    },
    '@container (max-width: 200px)': {
      [`& > .${chipClasses.root} > .${chipClasses.label}`]: {
        padding: theme.spacing(0, 2),
      },
    },
  },
  '&[data-badge-count="1"]': {
    [`& > .${chipClasses.root}`]: {
      maxWidth: `calc(100% - ${BADGE_ITEM_WIDTH}px - ${theme.spacing(12)})`,
    },
  },
  '&[data-badge-count="2"]': {
    [`& > .${chipClasses.root}`]: {
      maxWidth: `calc(100% - ${BADGE_ITEM_WIDTH * 2}px - ${theme.spacing(21)})`,
    },
  },
});

const bottomPositionSx = (theme: Theme) => ({
  position: 'absolute',
  left: theme.spacing(2),
  bottom: theme.spacing(2),
  width: `calc(100% - ${theme.spacing(4)})`,
  pointerEvents: 'none',
  zIndex: 3,
});

export const StreamBadge = ({
  label,
  badgeCount,
  layout = VideoTileLayoutMode.top,
  ChipProps,
  sx = [],
  ...props
}: StreamBadgeProps) => {
  if (!label) {
    return null;
  }

  const isBottom = layout === VideoTileLayoutMode.bottom;

  return (
    <Box
      {...(badgeCount ? { 'data-badge-count': badgeCount } : {})}
      sx={[isBottom ? bottomPositionSx : topPositionSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    >
      <Chip label={label} {...ChipProps} />
    </Box>
  );
};
